/**
 * hrCycleReset.ts
 *
 * DANGEROUS — Destructive reset utility for hr_v1 cycle data. Wipes every
 * hr_v1 doc that belongs to one cycleId, so HR / dev can re-run the cycle
 * from a clean slate.
 *
 * Intended for development and staging. In production, deletion of an
 * active cycle should require explicit Firestore-rule unlock + audit, not
 * a single service call.
 *
 * Does NOT touch:
 *   - The ReviewCycle doc itself (only resets its currentStep + status)
 *   - OPTICS-side collections (reviewInvitations, reviewResponses, reviewForms)
 *   - Any cross-cycle template (eprFrameworkTemplates)
 */

import {
  collection, query, where, getDocs, writeBatch, doc, updateDoc,
  serverTimestamp, getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTIONS_BY_CYCLE = [
  'eprSubmissions',
  'review360Submissions',
  'magicLinkTokens',
  'calibrationAdjustments',
  'appraisalForms',
  'appealRecords',
  'outcomeRecords',
] as const;

const SINGLETON_COLLECTIONS_KEYED_BY_CYCLE = [
  'calibrationSessions',  // doc id = cycleId
] as const;

export interface ResetCycleResult {
  cycleId: string;
  deletedByCollection: Record<string, number>;
  totalDeleted: number;
}

export interface ResetCycleOptions {
  /** When true (default), also reset ReviewCycle.currentStep & status. */
  resetCycleDoc?: boolean;
  /** Caller userId for audit logging. */
  resetBy?: string;
}

/**
 * Delete every hr_v1 doc tied to this cycle.
 *
 * Batched at 500 deletes per write (Firestore max). Returns a per-collection
 * count of deletions.
 */
export async function resetCycleData(
  cycleId: string,
  opts: ResetCycleOptions = {},
): Promise<ResetCycleResult> {
  if (!cycleId) throw new Error('cycleId required');

  // Safety: refuse to reset a closed cycle unless explicitly forced via
  // the options. Closed cycles are typically already archived.
  const cycleSnap = await getDoc(doc(db, 'reviewCycles', cycleId));
  if (cycleSnap.exists()) {
    const status = (cycleSnap.data() as { status?: string }).status;
    if (status === 'closed') {
      throw new Error(
        `Refuse to reset: cycle '${cycleId}' is in status='closed'. ` +
        `Re-open it (set status back to 'active') if you really mean to.`,
      );
    }
  }

  const deletedByCollection: Record<string, number> = {};
  let totalDeleted = 0;

  for (const col of COLLECTIONS_BY_CYCLE) {
    const n = await deleteAllByCycleId(col, cycleId);
    deletedByCollection[col] = n;
    totalDeleted += n;
  }

  for (const col of SINGLETON_COLLECTIONS_KEYED_BY_CYCLE) {
    const ref = doc(db, col, cycleId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const batch = writeBatch(db);
      batch.delete(ref);
      await batch.commit();
      deletedByCollection[col] = 1;
      totalDeleted += 1;
    } else {
      deletedByCollection[col] = 0;
    }
  }

  if (opts.resetCycleDoc !== false) {
    await updateDoc(doc(db, 'reviewCycles', cycleId), {
      currentStep: null,
      status: 'draft',
      updatedAt: serverTimestamp(),
    });
  }

  return { cycleId, deletedByCollection, totalDeleted };
}

async function deleteAllByCycleId(col: string, cycleId: string): Promise<number> {
  let deletedCount = 0;
  // Loop until no docs left — handles cases > 500
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const q = query(collection(db, col), where('cycleId', '==', cycleId));
    const snap = await getDocs(q);
    if (snap.empty) break;

    const batch = writeBatch(db);
    let countInBatch = 0;
    for (const d of snap.docs) {
      batch.delete(d.ref);
      countInBatch += 1;
      if (countInBatch >= 500) break;
    }
    await batch.commit();
    deletedCount += countInBatch;
    if (snap.docs.length < 500) break;
  }
  return deletedCount;
}

/**
 * Reset only one reviewer's 360 submission + their magic-link token. Use
 * when one reviewer needs to re-do their feedback without wiping everything.
 */
export async function resetReviewer360(
  submissionId: string,
): Promise<void> {
  // Submission id = the doc id in review360Submissions
  const subRef = doc(db, 'review360Submissions', submissionId);
  const subSnap = await getDoc(subRef);
  if (!subSnap.exists()) {
    throw new Error(`360 submission '${submissionId}' not found.`);
  }
  const invitationId = (subSnap.data() as { invitationId?: string }).invitationId;

  const batch = writeBatch(db);
  batch.delete(subRef);
  if (invitationId) {
    batch.delete(doc(db, 'magicLinkTokens', invitationId));
  }
  await batch.commit();
}
